import React from 'react';
import PropTypes from 'prop-types';
import './SkeletonLoader.css';

const SkeletonLoader = ({ 
  variant = 'rectangular', 
  width = '100%', 
  height = '1rem',
  count = 1,
  className = '',
  animation = 'pulse',
  ...props 
}) => {
  const renderSkeleton = (index) => (
    <div
      key={index}
      className={`skeleton-loader skeleton-${variant} skeleton-${animation} ${className}`}
      style={{ width, height }}
      {...props}
    />
  );

  if (count === 1) {
    return renderSkeleton(0);
  }

  return (
    <div className="skeleton-container">
      {Array.from({ length: count }, (_, index) => renderSkeleton(index))}
    </div>
  );
};

SkeletonLoader.propTypes = {
  variant: PropTypes.oneOf(['rectangular', 'circular', 'rounded']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  count: PropTypes.number,
  className: PropTypes.string,
  animation: PropTypes.oneOf(['pulse', 'wave'])
};

// Componentes específicos para diferentes tipos de conteúdo
export const TextSkeleton = ({ lines = 3, ...props }) => (
  <div className="text-skeleton">
    {Array.from({ length: lines }, (_, index) => (
      <SkeletonLoader
        key={index}
        height="1rem"
        width={index === lines - 1 ? '75%' : '100%'}
        className="text-line"
        {...props}
      />
    ))}
  </div>
);

TextSkeleton.propTypes = {
  lines: PropTypes.number
};

export const CardSkeleton = ({ ...props }) => (
  <div className="card-skeleton">
    <SkeletonLoader variant="circular" width="40px" height="40px" className="avatar" />
    <div className="card-content">
      <SkeletonLoader height="1.2rem" width="60%" className="title" />
      <TextSkeleton lines={2} />
    </div>
  </div>
);

export const ButtonSkeleton = ({ ...props }) => (
  <SkeletonLoader
    variant="rounded"
    height="2.5rem"
    width="120px"
    className="button-skeleton"
    {...props}
  />
);

export const ImageSkeleton = ({ aspectRatio = '16/9', ...props }) => (
  <SkeletonLoader
    variant="rectangular"
    className="image-skeleton"
    style={{ aspectRatio }}
    {...props}
  />
);

ImageSkeleton.propTypes = {
  aspectRatio: PropTypes.string
};

export const ListSkeleton = ({ items = 5, ...props }) => (
  <div className="list-skeleton">
    {Array.from({ length: items }, (_, index) => (
      <div key={index} className="list-item-skeleton">
        <SkeletonLoader variant="circular" width="24px" height="24px" />
        <SkeletonLoader height="1rem" width="80%" />
      </div>
    ))}
  </div>
);

ListSkeleton.propTypes = {
  items: PropTypes.number
};

export const ChatSkeleton = ({ messages = 3, ...props }) => (
  <div className="chat-skeleton">
    {Array.from({ length: messages }, (_, index) => (
      <div key={index} className={`chat-message-skeleton ${index % 2 === 0 ? 'left' : 'right'}`}>
        <SkeletonLoader variant="rounded" height="2rem" width={`${60 + Math.random() * 30}%`} />
      </div>
    ))}
  </div>
);

ChatSkeleton.propTypes = {
  messages: PropTypes.number
};

export const DashboardSkeleton = () => (
  <div className="dashboard-skeleton">
    <div className="dashboard-header">
      <SkeletonLoader height="2rem" width="200px" className="page-title" />
      <ButtonSkeleton />
    </div>
    <div className="dashboard-stats">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="stat-card-skeleton">
          <SkeletonLoader variant="circular" width="48px" height="48px" />
          <div className="stat-content">
            <SkeletonLoader height="1.5rem" width="60px" />
            <SkeletonLoader height="0.875rem" width="80px" />
          </div>
        </div>
      ))}
    </div>
    <div className="dashboard-content">
      <CardSkeleton />
      <CardSkeleton />
    </div>
  </div>
);

export const PageSkeleton = () => (
  <div className="page-skeleton">
    <div className="page-header">
      <SkeletonLoader height="2.5rem" width="300px" className="page-title" />
      <SkeletonLoader height="1rem" width="500px" className="page-subtitle" />
    </div>
    <div className="page-content">
      <DashboardSkeleton />
    </div>
  </div>
);

// Exportar o componente base também
export { SkeletonLoader };

export default SkeletonLoader; 